import { useEffect, useRef } from "react";
import { Leaf } from "lucide-react";

const setTransparent = () => {
    const targets = [
        document.documentElement,
        document.body,
        document.getElementById("root"),
        document.querySelector(".App"),
    ].filter(Boolean);
    const prev = targets.map((t) => t.style.background);
    targets.forEach((t) => (t.style.background = "transparent"));
    return () => targets.forEach((t, i) => (t.style.background = prev[i] || ""));
};

/* Horizontal lockup — leaf + wordmark side-by-side */
export const LogoExport = () => {
    const ref = useRef(null);
    useEffect(() => {
        const restore = setTransparent();
        if (ref.current) ref.current.setAttribute("data-export-root", "true");
        return restore;
    }, []);

    return (
        <div
            ref={ref}
            style={{
                background: "transparent",
                padding: "32px",
                display: "inline-flex",
                alignItems: "center",
                gap: "14px",
                fontFamily: "'Cabinet Grotesk', 'Manrope', system-ui, sans-serif",
            }}
        >
            <Leaf
                color="#7BB893"
                strokeWidth={2.25}
                style={{ width: 96, height: 96 }}
            />
            <span
                style={{
                    fontSize: 132,
                    fontWeight: 800,
                    letterSpacing: "0.18em",
                    textTransform: "uppercase",
                    color: "#0F6B45",
                    lineHeight: 1,
                }}
            >
                Bushel
                <sup
                    style={{
                        fontSize: "0.42em",
                        fontWeight: 500,
                        letterSpacing: "normal",
                        verticalAlign: "super",
                    }}
                >
                    TM
                </sup>
            </span>
        </div>
    );
};

/* Square 300×300 lockup — leaf next to the wordmark (horizontal) */
export const LogoSquare = () => {
    const ref = useRef(null);
    useEffect(() => {
        const restore = setTransparent();
        if (ref.current) ref.current.setAttribute("data-export-root", "true");
        return restore;
    }, []);

    return (
        <div
            ref={ref}
            style={{
                background: "transparent",
                width: 300,
                height: 300,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                fontFamily: "'Cabinet Grotesk', 'Manrope', system-ui, sans-serif",
            }}
        >
            <Leaf
                color="#7BB893"
                strokeWidth={2.25}
                style={{ width: 46, height: 46, flexShrink: 0 }}
            />
            <span
                style={{
                    fontSize: 36,
                    fontWeight: 800,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "#0F6B45",
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                }}
            >
                Bushel
                <sup
                    style={{
                        fontSize: "0.42em",
                        fontWeight: 500,
                        letterSpacing: "normal",
                        verticalAlign: "super",
                    }}
                >
                    TM
                </sup>
            </span>
        </div>
    );
};

/* Square 512×512 favicon source — leaf in dark-green rounded square */
export const LogoFavicon = () => {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) ref.current.setAttribute("data-export-root", "true");
    }, []);

    return (
        <div
            ref={ref}
            style={{
                background: "#023020",
                width: 512,
                height: 512,
                borderRadius: 96,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
        >
            <Leaf
                color="#7BB893"
                strokeWidth={2.4}
                style={{ width: 320, height: 320 }}
            />
        </div>
    );
};

/* Square 300×300 with solid dark-green background — cream wordmark */
export const LogoSquareDark = () => {
    const ref = useRef(null);
    useEffect(() => {
        if (ref.current) ref.current.setAttribute("data-export-root", "true");
    }, []);

    return (
        <div
            ref={ref}
            style={{
                background: "#023020",
                width: 300,
                height: 300,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: 10,
                fontFamily: "'Cabinet Grotesk', 'Manrope', system-ui, sans-serif",
            }}
        >
            <Leaf
                color="#7BB893"
                strokeWidth={2.25}
                style={{ width: 46, height: 46, flexShrink: 0 }}
            />
            <span
                style={{
                    fontSize: 36,
                    fontWeight: 800,
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                    color: "#F7F9F5",
                    lineHeight: 1,
                    whiteSpace: "nowrap",
                }}
            >
                Bushel
                <sup
                    style={{
                        fontSize: "0.42em",
                        fontWeight: 500,
                        letterSpacing: "normal",
                        verticalAlign: "super",
                    }}
                >
                    TM
                </sup>
            </span>
        </div>
    );
};

export default LogoExport;
